import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Zap, ShieldCheck, BadgeCheck, Star, Clock, Loader2, Send,
  CheckCircle2, Stethoscope, Activity, Sparkles, RotateCcw,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'];
const CARE_TYPES = ['Skilled nursing (RN/LPN)', 'Personal care / aide (CNA)', 'Physical / Occupational therapy', 'Hospice & palliative', 'Companion care', 'Wound care', 'Not sure — need guidance'];

const VerifyBadge = ({ ok, label, icon: Icon }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${ok ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-cn-text-muted bg-cn-soft border-cn-border'}`}>
    <Icon className="w-3 h-3" /> {label}
  </span>
);

const MatchCard = ({ m, onRefer, referring, referred }) => {
  const initials = (m.name || 'C').split(' ').map((n) => n[0]).join('').slice(0, 2);
  return (
    <div className="rounded-2xl border border-cn-border bg-white dark:bg-[#0F172A] p-5 flex flex-col" data-testid={`discharge-match-${m.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-14 h-14 rounded-2xl overflow-hidden flex-shrink-0 bg-cn-sage-200 flex items-center justify-center text-white font-display font-bold text-lg">
          {m.photo_url ? <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover" /> : initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="font-display font-semibold text-cn-text leading-tight truncate">{m.name}</p>
            <span className="text-xs font-bold text-cn-primary bg-cn-soft px-2 py-0.5 rounded-full flex-shrink-0" data-testid={`match-score-${m.id}`}>
              {Math.round(m.match_score)}% match
            </span>
          </div>
          <p className="text-xs text-cn-text-muted">{m.license_type} · {m.years_of_experience || 0} yrs · {m.city}, {m.state}</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            <VerifyBadge ok={m.license_verified} label="License" icon={ShieldCheck} />
            <VerifyBadge ok={m.npi_verified} label="NPI" icon={BadgeCheck} />
            <VerifyBadge ok={m.background_check_status === 'passed'} label="Background" icon={ShieldCheck} />
          </div>
        </div>
      </div>

      {m.match_reasons?.length > 0 && (
        <ul className="mt-3 space-y-1">
          {m.match_reasons.slice(0, 3).map((r, i) => (
            <li key={i} className="text-xs text-cn-text flex items-start gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> {r}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex items-center justify-between text-xs text-cn-text-muted">
        <span className="inline-flex items-center gap-1">
          {m.rating ? <><Star className="w-3.5 h-3.5 fill-cn-terracotta-400 text-cn-terracotta-400" /> {m.rating} ({m.review_count})</> : 'New'}
        </span>
        {m.availability_status === 'available' && (
          <span className="inline-flex items-center gap-1 text-emerald-600"><Clock className="w-3.5 h-3.5" /> Available now</span>
        )}
      </div>

      <button
        onClick={() => onRefer(m)}
        disabled={referring || referred}
        className={`mt-4 cn-btn ${referred ? 'cn-btn-ghost text-emerald-600' : 'cn-btn-primary'} !py-2.5 text-sm w-full justify-center`}
        data-testid={`send-referral-${m.id}`}
      >
        {referring ? <Loader2 className="w-4 h-4 animate-spin" /> : referred ? <CheckCircle2 className="w-4 h-4" /> : <Send className="w-4 h-4" />}
        {referred ? 'Referral sent' : 'Send referral'}
      </button>
    </div>
  );
};

export const OneClickDischarge = ({ onReferred }) => {
  const { getAuthHeader } = useAuth();
  const [form, setForm] = useState({
    patient_initials: '', primary_diagnosis: '', care_type: '', state: '',
    city: '', visit_type: 'both', urgency: 'urgent', insurance: '', notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [referringId, setReferringId] = useState(null);
  const [referredIds, setReferredIds] = useState(new Set());
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === 'object' && v?.target ? v.target.value : v }));

  const findMatches = async () => {
    if (!form.primary_diagnosis.trim() || !form.state) {
      toast.error('Enter the primary diagnosis and state');
      return;
    }
    setLoading(true);
    setResult(null);
    setReferredIds(new Set());
    try {
      const { data } = await axios.post(`${API}/planner/discharge/match`, form, getAuthHeader());
      setResult(data);
      if (data.count === 0) toast.info('No verified matches in that state yet — try broadening visit type.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Match failed');
    } finally { setLoading(false); }
  };

  const refer = async (m) => {
    setReferringId(m.id);
    try {
      await axios.post(`${API}/planner/discharge/refer`, {
        ...form, clinician_id: m.id, clinician_name: m.name,
      }, getAuthHeader());
      setReferredIds((prev) => new Set(prev).add(m.id));
      toast.success(`Referral sent to ${m.name}`);
      onReferred && onReferred();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send referral');
    } finally { setReferringId(null); }
  };

  const reset = () => { setResult(null); setReferredIds(new Set()); };

  return (
    <div data-testid="one-click-discharge">
      <div className="mb-5">
        <h2 className="cn-display text-xl text-cn-text flex items-center gap-2">
          <Zap className="w-5 h-5 text-cn-terracotta-500" /> One-Click Discharge
        </h2>
        <p className="text-sm text-cn-text-muted">Enter the patient's discharge needs — get 3 verified, license-matched clinicians in seconds.</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Intake form */}
        <div className="lg:col-span-2">
          <div className="cn-card rounded-2xl p-5 space-y-4" data-testid="discharge-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Patient initials</Label>
                <Input value={form.patient_initials} onChange={set('patient_initials')} placeholder="J.D." data-testid="discharge-initials" />
              </div>
              <div>
                <Label className="text-xs">State *</Label>
                <Select value={form.state} onValueChange={set('state')}>
                  <SelectTrigger data-testid="discharge-state"><SelectValue placeholder="State" /></SelectTrigger>
                  <SelectContent className="max-h-64">{US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Primary diagnosis / reason *</Label>
              <Input value={form.primary_diagnosis} onChange={set('primary_diagnosis')} placeholder="e.g. Post-surgical wound care after hip replacement" data-testid="discharge-diagnosis" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Care type</Label>
                <Select value={form.care_type} onValueChange={set('care_type')}>
                  <SelectTrigger data-testid="discharge-care-type"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{CARE_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">City</Label>
                <Input value={form.city} onChange={set('city')} placeholder="Austin" data-testid="discharge-city" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Visit type</Label>
                <Select value={form.visit_type} onValueChange={set('visit_type')}>
                  <SelectTrigger data-testid="discharge-visit-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="both">In-person or telehealth</SelectItem>
                    <SelectItem value="in_person">In-person</SelectItem>
                    <SelectItem value="telehealth">Telehealth</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Urgency</Label>
                <Select value={form.urgency} onValueChange={set('urgency')}>
                  <SelectTrigger data-testid="discharge-urgency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">Urgent (24–48h)</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Insurance (optional)</Label>
              <Input value={form.insurance} onChange={set('insurance')} placeholder="Medicare, Aetna…" data-testid="discharge-insurance" />
            </div>
            <button onClick={findMatches} disabled={loading} className="cn-btn cn-btn-primary w-full justify-center !py-3" data-testid="discharge-find-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Find 3 verified matches
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-3">
          {!result && !loading && (
            <div className="cn-card rounded-2xl p-10 text-center h-full flex flex-col items-center justify-center" data-testid="discharge-empty">
              <div className="w-16 h-16 rounded-3xl bg-cn-soft text-cn-primary flex items-center justify-center mb-3"><Stethoscope className="w-8 h-8" /></div>
              <p className="text-cn-text font-medium">CaraMatch returns 3 verified clinicians instantly.</p>
              <p className="text-sm text-cn-text-muted mt-1 max-w-sm">Every match is license-verified and ranked by clinical fit, proximity, and availability.</p>
            </div>
          )}
          {loading && (
            <div className="cn-card rounded-2xl p-10 text-center h-full flex flex-col items-center justify-center" data-testid="discharge-loading">
              <Activity className="w-8 h-8 text-cn-primary animate-pulse mb-3" />
              <p className="text-cn-text font-medium">Running CaraMatch…</p>
            </div>
          )}
          {result && (
            <div data-testid="discharge-results">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <p className="text-sm text-cn-text-muted inline-flex items-center gap-1.5" data-testid="discharge-meta">
                  <Zap className="w-4 h-4 text-cn-terracotta-500" />
                  {result.count} verified {result.count === 1 ? 'match' : 'matches'} in {(result.elapsed_ms / 1000).toFixed(2)}s
                  <span className="text-cn-text-muted/70">· from {result.candidates_considered} in-state clinicians</span>
                </p>
                <button onClick={reset} className="text-xs text-cn-text-muted hover:text-cn-text inline-flex items-center gap-1" data-testid="discharge-reset">
                  <RotateCcw className="w-3.5 h-3.5" /> New search
                </button>
              </div>
              {result.count === 0 ? (
                <div className="cn-card rounded-2xl p-8 text-center text-cn-text-muted" data-testid="discharge-no-matches">
                  No verified clinicians in {form.state} yet for this need. Try "In-person or telehealth".
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {result.matches.map((m) => (
                    <MatchCard
                      key={m.id} m={m} onRefer={refer}
                      referring={referringId === m.id}
                      referred={referredIds.has(m.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
